// ============================================================================
//  FilePreviewModal — Visionneuse de fichiers plein écran (lecture seule)
//  PDF · images · texte brut dans l'application ; autres formats → repli
//  « Aperçu indisponible » avec téléchargement. Les URL sont présignées et
//  courtes : elles sont demandées à l'ouverture (et à chaque « Réessayer »).
// ============================================================================

import * as React from 'react'
import ReactDOM from 'react-dom'
import {
  AlertTriangle, Download, ExternalLink, File, FileArchive, FileImage,
  FileSpreadsheet, FileText, Loader2, Lock, RotateCw, X,
} from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Réponse attendue pour l'aperçu. `disposition` est la disposition réellement
 * accordée par le backend : 'attachment' si le type n'est pas affichable en ligne.
 */
export interface InlineUrlResult {
  url: string
  disposition?: 'inline' | 'attachment'
}

export interface FilePreviewModalProps {
  open: boolean
  onClose: () => void
  fileName: string
  mimeType?: string
  fileSize?: number
  /** URL présignée « inline » (affichage dans le navigateur) et disposition accordée. */
  getInlineUrl: () => Promise<InlineUrlResult>
  /** URL présignée « attachment » (déclenche un téléchargement). */
  getDownloadUrl: () => Promise<string>
}

type PreviewKind = 'pdf' | 'image' | 'text' | 'unsupported'

// Liste blanche exacte des images affichables. Exclus volontairement : SVG (un SVG
// ouvert dans un onglet exécute son script sur l'origine du stockage, qui peut être
// celle de l'application), TIFF, HEIC… → panneau « Aperçu indisponible ».
const PREVIEW_IMAGE_MIMES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp']

// Déduction par extension réservée aux images (type stocké absent ou générique)
const IMAGE_EXT_MIME: Record<string, string> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp',
}

const extOf = (fileName: string) => {
  const i = fileName.lastIndexOf('.')
  return i > 0 ? fileName.slice(i + 1).toLowerCase() : ''
}

const isGenericMime = (m: string) => !m || m === 'application/octet-stream'

/** Type MIME normalisé (sans paramètres) ; image déduite de l'extension si le type est générique. */
function effectiveMime(mimeType: string | undefined, fileName: string): string {
  const m = (mimeType ?? '').toLowerCase().split(';')[0].trim()
  return isGenericMime(m) ? IMAGE_EXT_MIME[extOf(fileName)] ?? m : m
}

/**
 * PDF et texte uniquement si le type stocké est exact : l'URL présignée renvoie ce
 * type (response-content-type), un octet-stream dans une iframe déclencherait un
 * téléchargement au lieu d'un affichage.
 */
function previewKind(mime: string): PreviewKind {
  if (mime === 'application/pdf') return 'pdf'
  if (mime === 'text/plain') return 'text'
  if (PREVIEW_IMAGE_MIMES.includes(mime)) return 'image'
  return 'unsupported'
}

function formatLabel(mime: string, fileName: string): string {
  if (mime === 'application/pdf') return 'PDF'
  if (mime === 'text/plain') return 'Texte'
  if (mime === 'text/csv') return 'CSV'
  if (mime.includes('wordprocessing') || mime === 'application/msword') return 'Word'
  if (mime.includes('spreadsheet') || mime.includes('ms-excel')) return 'Excel'
  if (mime.includes('presentation') || mime.includes('powerpoint')) return 'PowerPoint'
  if (mime.includes('zip') || mime.includes('compressed')) return 'Archive ZIP'
  if (mime.startsWith('image/')) return `Image ${(extOf(fileName) || mime.slice(6)).toUpperCase()}`
  return extOf(fileName).toUpperCase() || 'Fichier'
}

function FormatIcon({ mime, className }: { mime: string; className?: string }) {
  const [Icon, color] =
    mime === 'application/pdf' ? [FileText, '#F87171']
    : mime.startsWith('image/') ? [FileImage, '#C4B5FD']
    : mime.includes('spreadsheet') || mime.includes('ms-excel') || mime === 'text/csv' ? [FileSpreadsheet, '#6EE7B7']
    : mime.includes('zip') || mime.includes('compressed') ? [FileArchive, '#FCD34D']
    : mime.startsWith('text/') || mime.includes('word') ? [FileText, '#93C5FD']
    : [File, 'var(--sb-text)']
  return <Icon className={className} style={{ color }} aria-hidden="true" />
}

const fmtSize = (b: number) =>
  b < 1024 ? `${b} o`
  : b < 1024 * 1024 ? `${(b / 1024).toFixed(1).replace('.', ',')} Ko`
  : `${(b / 1024 / 1024).toFixed(1).replace('.', ',')} Mo`

/**
 * Déclenche le téléchargement d'une URL « attachment » sans ouvrir d'onglet :
 * la réponse porte Content-Disposition: attachment, le navigateur enregistre
 * le fichier et la page courante reste en place (pas de blocage pop-up).
 */
export function downloadFromUrl(url: string, fileName?: string) {
  const a = document.createElement('a')
  a.href = url
  if (fileName) a.download = fileName
  a.rel = 'noopener'
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

/** Message d'erreur lisible depuis une erreur axios (message backend), sinon `fallback`. */
export function errMessage(e: unknown, fallback: string): string {
  const m = (e as { response?: { data?: { message?: unknown } } } | null)?.response?.data?.message
  return Array.isArray(m) ? m.join(' ') : typeof m === 'string' && m ? m : fallback
}

// Boutons de la barre (fond sombre) — cibles tactiles 44 px sur mobile
const barBtn = [
  'inline-flex items-center justify-center gap-2 shrink-0 rounded-lg font-medium',
  'h-11 min-w-11 px-3 sm:h-9 sm:min-w-9 text-[13px] transition-colors',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sb-active-text)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--sb-bg)]',
  'disabled:opacity-40 disabled:cursor-not-allowed',
].join(' ')
const barBtnGhost = 'text-[var(--sb-text-h)] hover:bg-white/10 aria-disabled:opacity-40 aria-disabled:pointer-events-none'

/**
 * Le contenu n'est monté que pendant l'ouverture : chaque ouverture repart d'un état
 * vierge (aucune iframe / image rendue avec l'URL du fichier précédent).
 */
export function FilePreviewModal({ open, ...props }: FilePreviewModalProps) {
  if (!open) return null
  return <FilePreviewDialog {...props} />
}

function FilePreviewDialog({
  onClose, fileName, mimeType, fileSize, getInlineUrl, getDownloadUrl,
}: Omit<FilePreviewModalProps, 'open'>) {
  const titleId = React.useId()
  const descId = React.useId()
  const dialogRef = React.useRef<HTMLDivElement>(null)
  const closeBtnRef = React.useRef<HTMLButtonElement>(null)

  const mime = effectiveMime(mimeType, fileName)
  // Défense en profondeur : liste blanche côté client ET disposition accordée par le backend
  const clientKind = previewKind(mime)
  const [serverRefused, setServerRefused] = React.useState(false)
  const kind: PreviewKind = serverRefused ? 'unsupported' : clientKind

  const [status, setStatus] = React.useState<'loading' | 'ready' | 'error'>(clientKind === 'unsupported' ? 'ready' : 'loading')
  const [url, setUrl] = React.useState<string | null>(null)
  const [error, setError] = React.useState('')
  const [frameLoaded, setFrameLoaded] = React.useState(false)
  const [attempt, setAttempt] = React.useState(0)
  const [downloading, setDownloading] = React.useState(false)
  const [downloadError, setDownloadError] = React.useState('')

  // Callbacks via ref : les pages passent des fonctions inline, sans relancer l'effet
  const getInlineRef = React.useRef(getInlineUrl)
  getInlineRef.current = getInlineUrl
  const onCloseRef = React.useRef(onClose)
  onCloseRef.current = onClose

  // Chargement de l'URL d'aperçu (formats pris en charge uniquement)
  React.useEffect(() => {
    setUrl(null); setError(''); setFrameLoaded(false); setDownloadError(''); setServerRefused(false)
    if (clientKind === 'unsupported') { setStatus('ready'); return }
    setStatus('loading')
    let cancelled = false
    getInlineRef.current()
      .then(res => {
        if (cancelled) return
        // Le backend a forcé « attachment » : ne rien charger (ni iframe, ni img, ni lien)
        if (res.disposition === 'attachment') { setServerRefused(true); setStatus('ready'); return }
        setUrl(res.url); setStatus('ready')
      })
      .catch(e => {
        if (cancelled) return
        setError(errMessage(e, "Le lien d'aperçu n'a pas pu être obtenu."))
        setStatus('error')
      })
    return () => { cancelled = true }
  }, [clientKind, fileName, attempt])

  // Ouverture : focus sur « Fermer », fond inerte, page figée, Échap ; fermeture : focus rendu
  React.useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null
    const container = dialogRef.current?.parentElement
    // Fond inerte : piège le focus (y compris en sortie d'iframe) et masque le reste aux lecteurs d'écran
    const inerted: Element[] = []
    Array.from(document.body.children).forEach(el => {
      if (el !== container && !el.hasAttribute('inert')) { el.setAttribute('inert', ''); inerted.push(el) }
    })
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onCloseRef.current() }
    }
    document.addEventListener('keydown', onKey, true)
    closeBtnRef.current?.focus()
    return () => {
      document.removeEventListener('keydown', onKey, true)
      inerted.forEach(el => el.removeAttribute('inert'))
      document.body.style.overflow = prevOverflow
      if (previouslyFocused?.isConnected) previouslyFocused.focus()
    }
  }, [])

  const retry = () => setAttempt(a => a + 1)

  const download = async () => {
    setDownloading(true); setDownloadError('')
    try {
      // Nom du fichier : fourni par l'en-tête Content-Disposition de l'URL présignée
      downloadFromUrl(await getDownloadUrl())
    } catch (e) {
      setDownloadError(errMessage(e, 'Le téléchargement a échoué. Réessayez.'))
    } finally {
      setDownloading(false)
    }
  }

  const label = formatLabel(mime, fileName)
  const canOpenInTab = kind !== 'unsupported' && status === 'ready' && !!url

  const onStageClick = (e: React.MouseEvent) => { if (e.target === e.currentTarget) onClose() }

  const loadingState = (
    <div className="flex flex-col items-center gap-3 text-[var(--sb-text)]" role="status">
      <Loader2 className="w-7 h-7 animate-spin text-[var(--sb-active-text)]" aria-hidden="true" />
      <span className="text-[13px]">Chargement de l'aperçu…</span>
    </div>
  )

  let body: React.ReactNode
  if (status === 'loading') {
    body = loadingState
  } else if (status === 'error') {
    body = (
      <div className="max-w-sm text-center px-6 py-8 rounded-2xl bg-white/[0.04] border border-[var(--sb-border)]" role="alert">
        <AlertTriangle className="w-8 h-8 mx-auto mb-3 text-[#FCD34D]" aria-hidden="true" />
        <p className="text-[15px] font-semibold text-[var(--sb-text-h)]">Aperçu impossible</p>
        <p className="mt-1.5 text-[13px] leading-relaxed text-[var(--sb-text)]">{error}</p>
        <button type="button" onClick={retry} className={cn(barBtn, 'mt-5 bg-white text-[var(--sb-bg)] hover:bg-white/90')}>
          <RotateCw className="w-4 h-4" aria-hidden="true" /> Réessayer
        </button>
      </div>
    )
  } else if (kind === 'unsupported') {
    body = (
      <div className="max-w-sm text-center px-6 py-8 rounded-2xl bg-white/[0.04] border border-[var(--sb-border)]">
        <FormatIcon mime={mime} className="w-12 h-12 mx-auto mb-4" />
        <p className="text-[15px] font-semibold text-[var(--sb-text-h)]">Aperçu indisponible pour ce format</p>
        <p className="mt-1.5 text-[13px] leading-relaxed text-[var(--sb-text)]">
          Les fichiers {label} ne s'affichent pas dans l'application. Téléchargez le fichier pour l'ouvrir avec le logiciel adapté.
        </p>
        <button
          type="button" onClick={download} disabled={downloading}
          className={cn(barBtn, 'mt-5 text-white')}
          style={{ background: 'linear-gradient(135deg, var(--brand-orange) 0%, var(--brand-amber) 100%)' }}
        >
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Download className="w-4 h-4" aria-hidden="true" />}
          Télécharger
        </button>
      </div>
    )
  } else if (kind === 'image') {
    body = (
      <img
        src={url!}
        alt={`Aperçu de ${fileName}`}
        onError={() => {
          setError("L'image n'a pas pu être chargée : le lien a peut-être expiré. Réessayez pour en obtenir un nouveau.")
          setStatus('error')
        }}
        className="max-w-full max-h-full object-contain rounded-md shadow-[0_24px_64px_rgba(0,0,0,0.45)] select-none"
        draggable={false}
      />
    )
  } else {
    // PDF : barre d'outils et panneau latéral du lecteur masqués (lecture seule visuelle)
    const src = kind === 'pdf' ? `${url}#toolbar=0&navpanes=0` : url!
    body = (
      <div className={cn('relative w-full h-full', kind === 'text' ? 'max-w-[880px]' : 'max-w-[1100px]')}>
        {!frameLoaded && <div className="absolute inset-0 flex items-center justify-center">{loadingState}</div>}
        {/* Texte : iframe sandboxée (scripts, formulaires, pop-ups et navigation bloqués).
            « allow-same-origin » est nécessaire : avec un sandbox vide (origine opaque),
            Chromium n'affiche pas le document text/plain. Sans « allow-scripts », cette
            permission ne donne aucun accès à du code. PDF : pas de sandbox, le lecteur
            PDF intégré de Chrome refuse de s'afficher dans une iframe sandboxée. */}
        <iframe
          title={`Aperçu de ${fileName}`}
          src={src}
          sandbox={kind === 'text' ? 'allow-same-origin' : undefined}
          onLoad={() => setFrameLoaded(true)}
          className={cn(
            'w-full h-full border-0 rounded-md bg-white shadow-[0_24px_64px_rgba(0,0,0,0.45)] transition-opacity duration-200',
            frameLoaded ? 'opacity-100' : 'opacity-0',
          )}
        />
      </div>
    )
  }

  const dialog = (
    <div className="fixed inset-0 z-[99999]" style={{ isolation: 'isolate' }}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="nx-fade-in motion-reduce:animate-none flex flex-col w-full h-full bg-[var(--sb-bg-header)] overscroll-contain"
      >
        {/* Barre d'en-tête */}
        <header className="flex items-center gap-3 px-3 sm:px-5 py-2 sm:py-2.5 bg-[var(--sb-bg)] border-b border-[var(--sb-border)] shrink-0">
          <span className="hidden sm:flex items-center justify-center w-9 h-9 rounded-lg bg-white/[0.06] shrink-0">
            <FormatIcon mime={mime} className="w-[18px] h-[18px]" />
          </span>
          <div className="flex-1 min-w-0">
            <h2 id={titleId} className="text-[14px] font-semibold text-[var(--sb-text-h)] truncate font-[Raleway]" title={fileName}>
              {fileName}
            </h2>
            <p id={descId} className="flex items-center gap-2 text-[12px] text-[var(--sb-text)] mt-0.5">
              <span>{label}</span>
              {fileSize !== undefined && fileSize > 0 && <span>{fmtSize(fileSize)}</span>}
              <span className="inline-flex items-center gap-1">
                <Lock className="w-3 h-3" aria-hidden="true" /> Lecture seule
              </span>
            </p>
            {downloadError && (
              <p role="alert" className="text-[12px] text-[#FCA5A5] mt-0.5 truncate">{downloadError}</p>
            )}
          </div>

          <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
            <button
              type="button" onClick={download} disabled={downloading}
              aria-label="Télécharger" title="Télécharger"
              className={cn(barBtn, 'text-white')}
              style={{ background: 'linear-gradient(135deg, var(--brand-orange) 0%, var(--brand-amber) 100%)' }}
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Download className="w-4 h-4" aria-hidden="true" />}
              <span className="hidden sm:inline">Télécharger</span>
            </button>
            {kind !== 'unsupported' && (
              canOpenInTab ? (
                <a
                  href={url!} target="_blank" rel="noopener noreferrer"
                  aria-label="Ouvrir dans un onglet" title="Ouvrir dans un onglet"
                  className={cn(barBtn, barBtnGhost)}
                >
                  <ExternalLink className="w-4 h-4" aria-hidden="true" />
                  <span className="hidden md:inline">Ouvrir dans un onglet</span>
                </a>
              ) : (
                <button type="button" disabled aria-label="Ouvrir dans un onglet" className={cn(barBtn, barBtnGhost)}>
                  <ExternalLink className="w-4 h-4" aria-hidden="true" />
                  <span className="hidden md:inline">Ouvrir dans un onglet</span>
                </button>
              )
            )}
            <span className="w-px h-6 bg-[var(--sb-divider)] mx-0.5 hidden sm:block" aria-hidden="true" />
            <button
              ref={closeBtnRef} type="button" onClick={onClose}
              aria-label="Fermer l'aperçu" title="Fermer (Échap)"
              className={cn(barBtn, barBtnGhost, 'px-0 w-11 sm:w-9')}
            >
              <X className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        </header>

        {/* Plan de lecture : clic sur le fond = fermeture */}
        <div
          onClick={onStageClick}
          className="flex-1 min-h-0 flex items-center justify-center p-2 sm:p-6 overflow-hidden overscroll-contain"
        >
          {body}
        </div>
      </div>
    </div>
  )

  return ReactDOM.createPortal(dialog, document.body)
}
